import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PortableText, PortableTextReactComponents } from "@portabletext/react";
import { Helmet } from "react-helmet-async";
import { client } from "../sanity/client";
import { urlFor } from "../sanity/image";
import { POST_BY_SLUG } from "../sanity/queries";

import "./blog.theme.css";
import Button from "../shared/button/Button";
import { ArrowLeftIcon, CalendarDotsIcon } from "@phosphor-icons/react/dist/ssr";
import Title from "../components/typography/Title";
import InitialsLogo from "../shared/InitialsLogo";
import Subtitle from "../components/typography/Subtitle";
import Description from "../components/typography/Description";

// ---------- Category mapping for SEO breadcrumbs ONLY (not visible) ----------
const CATEGORY_MAP: Record<string, string> = {
  ai: "AI",
  product: "Product",
  blog: "Blogs",
  news: "News",
  "case-study": "Customer Gallery",
  "customer-gallery": "Customer Gallery",
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

type SanityImage = {
  alt?: string;
  asset?: {
    url?: string;
    metadata?: { dimensions?: { width?: number; height?: number } };
  };
};

// Google truncates meta descriptions around 155-160 characters. The CMS excerpt
// is written for blog cards and runs longer than that (median ~218 chars here),
// so trim it on a word boundary rather than letting the SERP cut mid-word.
const toMetaDescription = (text?: string, max = 155): string | undefined => {
  if (!text) return undefined;
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (!clean) return undefined;
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[,;:.\-–—]+$/, "")}…`;
};

type SanityFile = {
  asset?: {
    _id?: string;
    url?: string;
  };
};

type TocSchemaItem = {
  title?: string;
  anchor?: string;
  subItems?: { title?: string; anchor?: string }[];
};

type TocUiItem = {
  id: string;
  title: string;
  children: { id: string; title: string }[];
};

type Post = {
  _id: string;
  title: string;
  slug: string;
  date?: string;
  coverImage?: any;
  author?: { name?: string } | null;
  content?: any;
  stats?: { value: string; label?: string }[];
  company?: { logo: string; industry: string; headquarters: string };

  excerpt?: string;

  // SEO
  tabTitle?: string;
  seoTitle?: string;
  seoDescription?: string;
  seoImage?: SanityImage | null;
  seoImages?: SanityImage[] | null;
  canonical?: string;
  noIndex?: boolean;
  aiSummary?: string;
  focusKeyword?: string;
  metaKeywords?: string[];
  breadcrumbLabel?: string;
  h1Override?: string;

  // LLM
  aiSummaryLong?: string;
  llmTags?: string[];
  llmDifficulty?: string;
  llmEntities?: string[];
  llmQuestions?: string[];
  llmTakeaways?: string[];

  // Video – hero + legacy
  heroVideoUrl?: string;
  heroVideoFile?: SanityFile | null;
  videoUrl?: string;

  // tags for category mapping
  tags?: string[];

  // Table of contents from Sanity
  tableOfContents?: TocSchemaItem[];
};

export default function BlogPost() {
  const { slug = "" } = useParams();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);

  // ------- Fetch from Sanity -------
  useEffect(() => {
    const s = decodeURIComponent(slug);
    setLoading(true);
    client
      .fetch<Post | null>(POST_BY_SLUG, { slug: s })
      .then((r) => setPost(r))
      .finally(() => setLoading(false));
  }, [slug]);

  // ------- Category (for SEO breadcrumb only) -------
  const categoryName = useMemo(() => {
    if (!post?.tags?.length) return "Blogs";
    for (const t of post.tags) {
      if (CATEGORY_MAP[t]) return CATEGORY_MAP[t];
    }
    return "Blogs";
  }, [post]);

  // ✅ HERO image
  const HERO_W = 1160;
  const heroSrc = post?.coverImage
    ? urlFor(post?.coverImage).width(HERO_W).auto("format").url()
    : undefined;

  const dateText =
    post?.date &&
    new Date(post?.date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  // ------- Video handling (for hero) -------
  const getYouTubeId = (url: string) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com.*v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    return match ? match[1] : null;
  };

  // prefer the new hero fields, fall back to old videoUrl if needed
  const heroVideoFileUrl = post?.heroVideoFile?.asset?.url || null;
  const heroVideoUrl = post?.heroVideoUrl || post?.videoUrl || null;
  const heroYouTubeId = heroVideoUrl ? getYouTubeId(heroVideoUrl) : null;

  // ✅ SEO helpers
  const baseUrl = "https://babblebots.ai";

  const canonical = (
    post?.canonical || `${baseUrl}/blog/${post?.slug}`
  ).replace(/\/$/, "");

  const primaryImage =
    post?.seoImage?.asset?.url || heroSrc || `${baseUrl}/default-og.jpg`;

  const metaTitle =
    post?.tabTitle || post?.seoTitle || post?.title || "Babblebots Blog";

  // The generic string is a last resort only. Before today it was hitting 33 of
  // 70 posts, giving a third of the blog one identical description in search
  // results — those pages were earning ~5k impressions a month at under 1% CTR.
  const metaDesc =
    post?.seoDescription ||
    post?.aiSummary ||
    toMetaDescription(post?.excerpt) ||
    "AI recruitment insights and best practices from Babblebots.ai";

  // Build list of all OG images (only valid urls)
  const ogImages = [post?.seoImage, ...(post?.seoImages || [])].filter(
    (img): img is SanityImage => !!img?.asset?.url
  );

  // ✅ DO-OR-DIE: remove manual document.title override (Helmet must own it)
  // useEffect(() => {
  //   if (metaTitle) document.title = metaTitle;
  // }, [metaTitle]);

  // ---------- Build TOC UI items ----------
  const tocItems: TocUiItem[] = useMemo(() => {
    if (post?.tableOfContents && post.tableOfContents.length > 0) {
      return post.tableOfContents.map((item) => {
        const title = item.title || "";
        const id = item.anchor || slugify(title);
        const children =
          item.subItems?.map((sub) => ({
            title: sub.title || "",
            id: sub.anchor || slugify(sub.title || ""),
          })) || [];
        return { id, title, children };
      });
    }

    if (!post?.content) return [];

    const toc: TocUiItem[] = [];
    let currentParent: TocUiItem | null = null;

    post.content.forEach((block: any) => {
      if (block._type !== "block") return;
      const style = block.style;
      if (style !== "h2" && style !== "h3") return;

      const text = (block.children || [])
        .map((c: any) => c.text)
        .join(" ")
        .trim();
      if (!text) return;

      const id = slugify(text);

      if (style === "h2") {
        const parent: TocUiItem = { id, title: text, children: [] };
        toc.push(parent);
        currentParent = parent;
      } else if (style === "h3") {
        const child = { id, title: text };
        if (currentParent) currentParent.children.push(child);
        else toc.push({ id, title: text, children: [] });
      }
    });

    return toc;
  }, [post]);

  // ---------- TOC open state ----------
  const [openSections, setOpenSections] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!tocItems || tocItems.length === 0) {
      setOpenSections({});
      return;
    }
    setOpenSections({ 0: true });
  }, [tocItems]);

  const toggleSection = (index: number) => {
    setOpenSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // ✅ Portable Text renderers (add videoEmbed support)
  const PT: Partial<PortableTextReactComponents> = {
    types: {
      image: ({ value }) => {
        if (!value?.asset) return null;
        const src = urlFor(value).width(1400).auto("format").url();
        return (
          <figure className="bb2-pt-figure">
            <img src={src} alt={value?.alt || post?.title} loading="lazy" />
            {value?.caption ? (
              <figcaption className="bb2-pt-caption">
                {value.caption}
              </figcaption>
            ) : null}
          </figure>
        );
      },
      videoEmbed: ({ value }) => {
        const url: string | undefined = value?.url;
        if (!url) return null;

        const ytId = getYouTubeId(url);

        if (ytId) {
          return (
            <div className="my-6 aspect-video w-full overflow-hidden rounded-xl">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${ytId}`}
                title={value?.title || post?.title || "Video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          );
        }

        return (
          <div className="my-6 w-full">
            <video controls className="h-auto w-full rounded-xl" src={url}>
              Your browser does not support the video tag.
            </video>
          </div>
        );
      },
    },
    marks: {
      link: ({ children, value }) => {
        const href = value?.href || "#";
        const external = /^https?:\/\//i.test(href);
        return (
          <a
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            className="text-blue-600 underline hover:text-blue-800"
          >
            {children}
          </a>
        );
      },
      strong: ({ children }) => <strong>{children}</strong>,
      em: ({ children }) => <em>{children}</em>,
    },
    list: {
      bullet: ({ children }) => (
        <ul className="my-2 ml-4 list-disc">{children}</ul>
      ),
      number: ({ children }) => (
        <ol className="my-2 ml-4 list-decimal">{children}</ol>
      ),
      checkmarks: ({ children }) => (
        <ol className="m-auto text-lg">{children}</ol>
      ),
    },
    block: {
      h2: ({ children }) => {
        const text = String(children).trim();
        const id = slugify(text);
        return (
          <h2
            id={id}
            className="mb-2 mt-8 text-left text-3xl font-semibold text-gray-800"
          >
            {children}
          </h2>
        );
      },
      h3: ({ children }) => {
        const text = String(children).trim();
        const id = slugify(text);
        return (
          <h3
            id={id}
            className="mb-2 mt-6 text-left text-2xl font-medium text-gray-800"
          >
            {children}
          </h3>
        );
      },
    },
  };

  const authorName = post?.author?.name || "Team Babblebots";
  const publisherName = "Babblebots.ai";

  const initials = (authorName || "Team")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  // ---------- Breadcrumb data (SEO only, NOT rendered) ----------
  const breadcrumb = [
    { label: "Home", href: "/" },
    { label: "Resources", href: "/resources" },
    {
      label: categoryName,
      href: `/resources?cat=${encodeURIComponent(categoryName)}`,
    },
    { label: post?.breadcrumbLabel || post?.title || "", href: canonical },
  ];

  if (loading) {
    return (
      <main className="bb2-page">
        <div className="bb2-container">
          <div className="bb2-glass">Loading…</div>
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="bb2-page">
        <div className="bb2-container">
          <div className="bb2-glass">Post not found.</div>
        </div>
      </main>
    );
  }

  const displayH1 = post.h1Override || post.title;

  return (
    <>
      {/* ✅ SEO Helmet */}
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />

        {/* ✅ DO-OR-DIE: satisfy SEO tools */}
        <meta name="author" content={authorName} />
        <meta name="publisher" content={publisherName} />
        <meta property="article:author" content={authorName} />
        <meta property="article:publisher" content={baseUrl} />
        {post?.date && <meta property="article:published_time" content={post.date} />}
        {post?.date && <meta property="article:modified_time" content={post.date} />}

        {post.metaKeywords && post.metaKeywords.length > 0 && (
          <meta name="keywords" content={post.metaKeywords.join(", ")} />
        )}
        {post.focusKeyword && (
          <meta name="article:section" content={post.focusKeyword} />
        )}

        <link rel="canonical" href={canonical} />

        <meta
          name="robots"
          content={post?.noIndex ? "noindex, nofollow" : "index, follow"}
        />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="Babblebots.ai" />

        {/* ✅ DO-OR-DIE: always output a valid og:image */}
        <meta
          property="og:image"
          content={ogImages[0]?.asset?.url || primaryImage}
        />

        {ogImages.map((img, i) => (
          <React.Fragment key={i}>
            <meta property="og:image" content={img.asset?.url || ""} />
            {img.alt && <meta property="og:image:alt" content={img.alt} />}
            {img.asset?.metadata?.dimensions?.width && (
              <meta
                property="og:image:width"
                content={String(img.asset.metadata.dimensions.width)}
              />
            )}
            {img.asset?.metadata?.dimensions?.height && (
              <meta
                property="og:image:height"
                content={String(img.asset.metadata.dimensions.height)}
              />
            )}
          </React.Fragment>
        ))}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta
          name="twitter:image"
          content={ogImages[0]?.asset?.url || primaryImage}
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumb.map((b, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: b.label,
              item: b.href.startsWith("http") ? b.href : `${baseUrl}${b.href}`,
            })),
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post?.title,
            image: (ogImages.map((img) => img.asset?.url).filter(Boolean).length
              ? ogImages.map((img) => img.asset?.url).filter(Boolean)
              : [primaryImage]),
            author: {
              "@type": "Person",
              name: authorName,
            },
            publisher: {
              "@type": "Organization",
              name: publisherName,
              logo: {
                "@type": "ImageObject",
                url: `${baseUrl}/logominor.png`,
              },
            },
            datePublished: post?.date,
            dateModified: post?.date,
            description: metaDesc,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": canonical,
            },
          })}
        </script>

        {(heroVideoFileUrl || heroVideoUrl) && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "VideoObject",
              name: post.title,
              description: metaDesc,
              thumbnailUrl: ogImages[0]?.asset?.url || primaryImage,
              uploadDate: post.date,
              contentUrl: heroVideoFileUrl || heroVideoUrl,
              embedUrl: heroYouTubeId
                ? `https://www.youtube.com/embed/${heroYouTubeId}`
                : undefined,
            })}
          </script>
        )}
      </Helmet>

      {/* Page Content – VISUAL THEME UNCHANGED */}
      <main className="h-auto w-screen overflow-y-auto">
        {/* ---------- HERO SECTION ---------- */}
        <section className="relative flex h-auto w-screen items-center justify-center gap-10 overflow-hidden px-4 py-5 sm:px-6 md:h-[70vh] md:px-10">
          <div className="gradient-bg absolute left-0 top-2/3 h-1/2 w-full md:left-1/2 md:h-[65%] md:w-[65%] md:-translate-x-1/2" />
          <div className="z-30 w-full space-y-5">
            <Subtitle className="text-left">Resources</Subtitle>
            <Title as="h1" className="text-left">
             {displayH1}
            </Title>

            <div className="mb-4 mt-6">
              <InitialsLogo className="mr-4" name={initials} imgSrc={undefined} />
              <span>{authorName}</span>
            </div>
            <div className="flex w-full items-center gap-2">
              <CalendarDotsIcon size={16} color="#cfcfcf" />
              <span className="text-sm text-gray-300">{dateText}</span>
            </div>
          </div>

          <div className="z-30 h-full w-full rounded-xl">
            {heroVideoFileUrl ? (
              <video
                className="h-full w-full rounded-xl object-cover"
                src={heroVideoFileUrl}
                controls
                playsInline
                poster={heroSrc}
              >
                Your browser does not support the video tag.
              </video>
            ) : heroYouTubeId ? (
              <div className="h-full w-full overflow-hidden rounded-xl">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${heroYouTubeId}`}
                  title={post.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            ) : (
              heroSrc && (
                <img
                  className="h-auto max-h-full w-full rounded-xl object-contain"
                  src={heroSrc}
                  alt={post?.title}
                  sizes="(min-width: 1200px) 1160px, 100vw"
                />
              )
            )}
          </div>
        </section>

        {/* ---------- MAIN CONTENT + LEFT TOC ---------- */}
        <article className="flex w-screen flex-col bg-white px-4 py-10 text-black sm:flex-row sm:px-6 sm:pt-20 lg:px-32">
          <aside className="mb-8 w-full sm:mb-0 sm:w-1/3 sm:pr-8 lg:w-1/4">
            <Link to="/resources">
              <Button
                variant="secondary"
                className="border-gray-450 !text-black hover:bg-gray-200 hover:shadow-md"
                icon={<ArrowLeftIcon size={20} />}
              >
                <span className="sr-only">Back to Blogs</span>
              </Button>
            </Link>

            {displayH1 && (
             <div className="mt-6 text-lg font-semibold leading-snug text-gray-900">
             {displayH1}
             </div>
            )}


            {tocItems.length > 0 && (
              <div className="mt-6 rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="border-b px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Table of Contents
                </div>

                <div className="divide-y">
                  {tocItems.map((item, index) => {
                    const hasChildren = item.children.length > 0;
                    const isOpen = !!openSections[index];

                    return (
                      <div key={item.id} className="px-3 py-2 text-sm">
                        <div className="flex items-center justify-between">
                          <a
                            href={`#${item.id}`}
                            className="flex-1 text-left text-gray-800 hover:text-black"
                          >
                            {item.title}
                          </a>
                          {hasChildren && (
                            <button
                              type="button"
                              onClick={() => toggleSection(index)}
                              className="ml-2 text-gray-500 hover:text-black"
                              aria-label="Toggle section"
                            >
                              {isOpen ? "▾" : "▸"}
                            </button>
                          )}
                        </div>

                        {hasChildren && isOpen && (
                          <ul className="mt-2 space-y-1 border-l border-gray-200 pl-3 text-xs text-gray-600">
                            {item.children.map((child) => (
                              <li key={child.id}>
                                <a href={`#${child.id}`} className="hover:text-black">
                                  {child.title}
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </aside>

          <div className="w-full sm:w-2/3 lg:w-3/4">
            <PortableText value={post?.content || []} components={PT} />
          </div>
        </article>
      </main>
    </>
  );
}
